import { useEffect, useRef, useState } from 'react';
import { AlertCircleIcon } from 'lucide-react';
import { Marker as MaplibreMarker, type MapMouseEvent, type Map as MaplibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { createOsmMap } from './base-map';
import { usePhotoMarkers, type PhotoLocation } from './use-photo-markers';

interface MapPinPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationChange: (lat: number, lng: number) => void;
  /** Photo locations to render as thumbnail markers on the map. */
  photoLocations?: PhotoLocation[];
}

const DEFAULT_LAT = -37.8136;
const DEFAULT_LNG = 144.9631;

/**
 * Small MapLibre GL map with a draggable pin for location selection.
 * Used in the report wizard. Also handles click-to-place.
 */
export function MapPinPicker({ latitude, longitude, onLocationChange, photoLocations = [] }: MapPinPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markerRef = useRef<MaplibreMarker | null>(null);
  const callbackRef = useRef(onLocationChange);
  const [mapError, setMapError] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Render photo thumbnail markers on the map.
  usePhotoMarkers(mapReady ? mapRef.current : null, photoLocations);

  useEffect(() => {
    callbackRef.current = onLocationChange;
  }, [onLocationChange]);

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const lat = latitude ?? DEFAULT_LAT;
    const lng = longitude ?? DEFAULT_LNG;

    let map: MaplibreMap;
    try {
      map = createOsmMap(containerRef.current, {
        center: [lng, lat],
        zoom: 15,
      });
    } catch {
      setMapError(true);
      return;
    }

    map.on('load', () => {
      const marker = new MaplibreMarker({ draggable: true, color: '#dc2626' })
        .setLngLat([lng, lat])
        .addTo(map);

      marker.on('dragend', () => {
        const ll = marker.getLngLat();
        callbackRef.current(ll.lat, ll.lng);
      });

      markerRef.current = marker;
      setMapReady(true);
    });

    map.on('click', (e: MapMouseEvent) => {
      if (markerRef.current) {
        markerRef.current.setLngLat(e.lngLat);
        callbackRef.current(e.lngLat.lat, e.lngLat.lng);
      }
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker when external position changes (e.g. GPS)
  useEffect(() => {
    if (markerRef.current && latitude != null && longitude != null) {
      markerRef.current.setLngLat([longitude, latitude]);
      mapRef.current?.easeTo({ center: [longitude, latitude], duration: 500 });
    }
  }, [latitude, longitude]);

  if (mapError) {
    return (
      <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-muted/50 p-6 text-center">
        <AlertCircleIcon className="size-8 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">Map unavailable</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your browser does not support WebGL or it may be disabled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full min-h-[280px] w-full overflow-hidden rounded-lg border border-border"
    />
  );
}
